import { useEffect, useRef, useState } from 'react';
import { Loader2, Search, UserSearch } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import LearnerJourneyPanel, { fetchLearnerJourney } from './LearnerJourneyPanel';

// Cohorts toolbar: find any learner by name / email / id and jump to their journey.
export default function LearnerSearch({ organizationId }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [openList, setOpenList] = useState(false);
  const [journey, setJourney] = useState(null);
  const box = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults(null); return undefined; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const org = organizationId && organizationId !== 'all' ? `&organization_id=${encodeURIComponent(organizationId)}` : '';
        const r = await api.get(`/segments/learners/search?q=${encodeURIComponent(term)}${org}`);
        if (!cancelled) { setResults(r.data); setOpenList(true); }
      } catch { if (!cancelled) setResults({ learners: [], total: 0 }); }
      finally { if (!cancelled) setSearching(false); }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, organizationId]);

  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpenList(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = async (l) => {
    setOpenList(false);
    setJourney({ loading: true, learner: l });
    try { setJourney(await fetchLearnerJourney(l.learner_key, organizationId)); }
    catch (err) { setJourney(null); toast.error(`Could not load journey: ${err.response?.data?.detail || err.message}`); }
  };

  return (
    <div ref={box} className="relative w-64" data-testid="learner-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results && setOpenList(true)} placeholder="Find a learner…" className="pl-9 pr-8" data-testid="learner-search-input" />
      {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />}
      {openList && results && (
        <ul className="absolute z-30 mt-1 w-[22rem] max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg text-xs divide-y divide-slate-100" data-testid="learner-search-results">
          {results.learners.length === 0 && <li className="px-3 py-3 text-slate-500" data-testid="learner-search-empty">No learners match “{q.trim()}”.</li>}
          {results.learners.map((l) => (
            <li key={l.learner_key}>
              <button type="button" onClick={() => pick(l)} className="w-full text-left px-3 py-2 hover:bg-teal-50" data-testid={`learner-search-result-${l.learner_key}`}>
                <p className="font-medium text-slate-800 truncate">{l.name || l.email || l.learner_id}</p>
                <p className="text-slate-500 truncate">{l.email || l.learner_id} · {l.cohorts.length} cohort{l.cohorts.length === 1 ? '' : 's'}: {l.cohorts.slice(0, 3).join(', ')}{l.cohorts.length > 3 ? '…' : ''}</p>
              </button>
            </li>
          ))}
          {results.total > results.learners.length && <li className="px-3 py-1.5 text-slate-400" data-testid="learner-search-more">{results.total - results.learners.length} more — refine your search</li>}
        </ul>
      )}
      <Dialog open={!!journey} onOpenChange={(o) => { if (!o) setJourney(null); }}>
        <DialogContent className="max-w-md" data-testid="learner-journey-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><UserSearch className="h-4 w-4" /> Learner journey</DialogTitle>
            <DialogDescription className="text-xs">Every cohort this learner joined and every programme reached through those cohorts, in date order.</DialogDescription>
          </DialogHeader>
          <LearnerJourneyPanel journey={journey} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
